import { FundAnalyticsData } from '../../_interfaces/fund-card/fund-analytics';
import { FundFactsData } from '../../_interfaces/fund-card/fund-facts';

/**
 * Calculate the domicile type from DAS domicile data in a defensive way in case any data changes.
 * @param domicile
 * @param domicileCode
 */
export const getDomicileType = (domicile: string, domicileCode: string) => {
	const standardDomiciles = [
		'Australia',
		'Brazil',
		'Canada',
		'Hong Kong',
		'Japan',
		'Mexico',
		'United States',
	];
	const standardDomicileCodes = ['AU', 'BR', 'CA', 'HK', 'JP', 'MX', 'US'];
	if (
		standardDomiciles.includes(domicile) ||
		standardDomicileCodes.includes(domicile) ||
		standardDomicileCodes.includes(domicileCode)
	) {
		return 'standard';
	} else return 'emea';
};

export const setFundAttributes = (
	fundCurrencyCode: string,
	shareClass: string,
	useOfProfits: string
) => {
	let text = '';
	if (fundCurrencyCode) text += fundCurrencyCode;
	if (shareClass && shareClass !== ' ')
		fundCurrencyCode ? (text += ` / ${shareClass}`) : (text += shareClass);
	if (useOfProfits && useOfProfits !== ' ')
		fundCurrencyCode || shareClass
			? (text += ` / ${useOfProfits}`)
			: (text += useOfProfits);
	return text;
};

// Adds dots to acronyms so that they are read out as letters by a screenreader instead of making them into strange words
export const addDotsToAcronym = (acronymString: string): string => {
	if (acronymString) {
		return Array.from(acronymString)
			.map((letter) => letter + '.')
			.join('');
	}
};

export const handleIfMissingPercentage = (value) => {
	if (value && value.slice(-1) !== '%' && value !== '-' && value !== ' ')
		return `${value}%`;
	else return value;
};

export const numberWithCommas = (number) => {
	if (number) return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const getEsgBasedOnHoldingsDate = (esgAnalytics: FundAnalyticsData) => {
	if (esgAnalytics?.emeaHoldingsDate === undefined) {
		return esgAnalytics?.holdingsDate?.formattedValue;
	} else {
		return esgAnalytics?.emeaHoldingsDate?.formattedValue;
	}
};

export const setEsgDataPoints = (
	domicileType,
	domicile,
	esgAnalytics: FundAnalyticsData,
	keyfundfacts: FundFactsData
) => {
	return [
		{
			label: esgAnalytics?.emeaEsgRating?.label,
			tooltip: esgAnalytics?.emeaEsgRating?.infoBubble,
			asOf: esgAnalytics?.emeaEsgRating?.formattedAsOfDate,
			flag: esgAnalytics?.emeaEsgRating?.formattedValue,
			value: esgAnalytics?.emeaEsgRating?.formattedValue,
			prefix: esgAnalytics?.emeaEsgRating?.prefix,
		},
		{
			label: esgAnalytics?.emeaEsgQualityScore?.label,
			tooltip: esgAnalytics?.emeaEsgQualityScore?.infoBubble,
			asOf: esgAnalytics?.emeaEsgQualityScore?.formattedAsOfDate,
			flag: esgAnalytics?.emeaEsgQualityScore?.formattedValue,
			value: esgAnalytics?.emeaEsgQualityScore?.formattedValue,
			prefix: esgAnalytics?.emeaEsgQualityScore?.prefix,
		},
		{
			label: esgAnalytics?.emeaWeightedAverageCarbon?.label,
			tooltip: esgAnalytics?.emeaWeightedAverageCarbon?.infoBubble,
			asOf: esgAnalytics?.emeaWeightedAverageCarbon?.formattedAsOfDate,
			flag: esgAnalytics?.emeaWeightedAverageCarbon?.formattedValue,
			value: esgAnalytics?.emeaWeightedAverageCarbon?.formattedValue,
			prefix: esgAnalytics?.emeaWeightedAverageCarbon?.prefix,
		},
		{
			label: esgAnalytics?.wtavgCarbCoverage?.label,
			tooltip: esgAnalytics?.wtavgCarbCoverage?.infoBubble,
			asOf: esgAnalytics?.wtavgCarbCoverage?.formattedAsOfDate,
			flag: esgAnalytics?.wtavgCarbCoverage?.formattedValue,
			value: esgAnalytics?.wtavgCarbCoverage?.formattedValue,
			prefix: esgAnalytics?.wtavgCarbCoverage?.prefix,
		},
		{
			label: esgAnalytics?.emeaEsgCoverage?.label,
			tooltip: esgAnalytics?.emeaEsgCoverage?.infoBubble,
			asOf: esgAnalytics?.emeaEsgCoverage?.formattedAsOfDate,
			flag:
				esgAnalytics?.emeaEsgCoverage?.formattedValue &&
				domicile === 'United States',
			value: esgAnalytics?.emeaEsgCoverage?.formattedValue,
			prefix: esgAnalytics?.emeaEsgCoverage?.prefix,
		},
		{
			label: keyfundfacts?.sfdr?.label,
			tooltip: keyfundfacts?.sfdr?.infoBubble,
			asOf: keyfundfacts?.sfdr?.formattedAsOfDate,
			flag: keyfundfacts?.sfdr?.value && domicileType === 'emea',
			value: keyfundfacts?.sfdr?.value,
			prefix: keyfundfacts?.sfdr?.prefix,
			sfdr: true,
		},
	];
};

// Sfdr values should not have a date rendered in the tooltip. For that reason I've set "sfdr: true" in the object that contains sfdr values, and added invalid paramater to the formating function.
export const formatTooltipDate = (
	label: string,
	date: string,
	invalid: boolean
) => {
	if (invalid) return '';
	else if (date) return `<br> ${label} ${date}`;
	else return '';
};

// needed for animations to work for the esg dropdown in the fund card in the featured funds and in a standalone component
export const setDisplayStyle = (activeDataPoints, esgVisibility) => {
	const flex = { display: 'flex' };
	const none = { display: 'none' };
	if (activeDataPoints === 'esg' || esgVisibility === 'flex') return flex;
	else return none;
};
